/** @format */

import {useState} from 'react';
import dot from 'dot-object';
import {useForm, parseEntityError} from 'frontier-form';
import SuperAgent from 'superagent';
import update from 'immutability-helper';

export function useAddressForm(
    {onSaveComplete, userId = false, ...props} = {onSaveComplete: () => {}}
) {
    const {formData, ...formFunctions} = useForm({
        is_default_address: props.is_default_address ?? false,
        is_shipping_address: props.is_shipping_address ?? false,
        is_enabled: props.is_enabled ?? true,
        is_blind: props.is_blind ?? false,
        proof_email: props.proof_email ?? '',
        full_name: props.full_name ?? '',
        first_name: props.first_name ?? '',
        last_name: props.last_name ?? '',
        country: props.country ?? 'US',
        formatted_address: props.formatted_address ?? '',
        address1: props.address1 ?? '',
        address2: props.address2 ?? '',
        company: props.company ?? '',
        state: props.state ?? '',
        phone: props.phone ?? '',
        email: props.email ?? '',
        city: props.city ?? '',
        zip: props.zip ?? '',
        id: props.id ?? ''
    });

    const [candidates, setCandidates] = useState([]);
    const [addressFormErrors, setAddressFormErrors] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [validationStatus, setValidationStatus] = useState(false);

    async function onValidate() {
        setValidationStatus(false);
        setAddressFormErrors(false);
        setIsSaving(true);

        try {
            const {
                body: {
                    address,
                    inferredComponents,
                    isFixRequired,
                    isConfirmationNeeded,
                    missingFields,
                    original,
                    replacedComponents
                }
            } = await SuperAgent.post('/account/addresses/validate')
                .set('accept', 'application/json')
                .send({...formData});

            if (!isFixRequired && !isConfirmationNeeded) {
                // 3981 Prairie creek new braunfels tx 78132
                const {postalAddress} = address;
                const address2 =
                    postalAddress.addressLines.length > 1 ? postalAddress.addressLines.pop() : '';
                await onUpdateAndSave({
                    address1: {$set: postalAddress.addressLines.join(' ')},
                    address2: {$set: address2},
                    city: {$set: postalAddress.locality},
                    state: {$set: postalAddress.administrativeArea},
                    zip: {$set: postalAddress.postalCode}
                });
            } else {
                setValidationStatus({
                    address,
                    inferredComponents,
                    isFixRequired,
                    missingFields,
                    original,
                    replacedComponents
                });
                setIsSaving(false);
            }
        } catch (e) {
            setIsSaving(false);
            setAddressFormErrors(parseEntityError(dot.pick('response.body', e)));
        }
    }

    async function onUpdateAndSave(formDataTarget) {
        const updatedFormData = update(formData, formDataTarget);
        formFunctions.setFormData(updatedFormData);
        await save(updatedFormData);
    }

    async function onSave() {
        await save(formData);
    }

    async function save(formData) {
        setValidationStatus(false);
        setIsSaving(true);

        try {
            const endpoint = formData.id
                ? `/account/addresses/edit/${formData.id}`
                : `/account/addresses/add`;
            const response = await SuperAgent.post(endpoint)
                .set('accept', 'application/json')
                .send({...formData, userId});

            setIsSaving(false);
            setAddressFormErrors(false);
            onSaveComplete(response.body);
        } catch (e) {
            setIsSaving(false);
            let error = e;
            if (e?.response?.body) {
                error = parseEntityError(dot.pick('response.body', e));
            }
            setAddressFormErrors(error);
        }
    }

    return {
        ...formFunctions,
        formData,
        isSaving,
        addressFormErrors,
        setAddressFormErrors,
        onUpdateAndSave,
        onSave,
        validationStatus,
        onValidate
    };
}
